import {datadogRum} from '@datadog/browser-rum';
import {push} from 'connected-react-router';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {
  initiateTokenRefresh,
  toggleRefreshSpinner,
  tokenRefresh,
  TokenRefreshActionTypes,
  TokenRefreshLocalKey,
  TokenRefreshSessionKeys,
  TokenSet,
} from 'Core/ducks/refreshToken';
import {fromResult} from 'Core/models/Async';
import {
  getOAuthVariablesForAgentType,
  setTokenRefreshFlag,
  shouldRefreshToken,
  updateLocalStorage,
} from 'Core/refreshTokenUtilityMethods';
import {findFirst} from 'fp-ts/lib/Array';
import {chain, fromNullable, isSome, none, some} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {OAuthTokenGeneration} from 'Core/appfabric/OAuthTokenGeneration';
import {OAuthToken} from 'Core/appfabric/OAuthTokenSingleton';
import {Dispatch} from 'redux';
import {all, call, put, takeLatest} from 'redux-saga/effects';
import {getUserName} from 'Shared/utilities/Auth';
import axios from 'Shared/utilities/axiosInstance';
import {eq} from 'Shared/utilities/equals';
import {getUserClaims} from 'Shared/utilities/UserProfileUtilities';
import {clearEmulation} from 'Users/ducks/agentAdministration';
import {setSelectedAgency, setUserClaims} from 'Users/ducks/userProfile';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import {getOAuthProviderVariablesExternal, getOAuthProviderVariablesInternal} from '../../OAuthProviderVariables';
import {toggleLoginSpinner} from '../ducks/login';
import {hideModal} from '../ducks/modal';

/**
 * Fetches the claims data from the claimset api for large claims
 * @param accessToken the jwt access token
 * @returns claims data fetched from the claimset api
 */
async function fetchLargeClaims(accessToken: string) {
  const {data} = await axios.get('/api/user/v1/jwtclaimset', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return JSON.stringify(data);
}

/**
 * Reads user claims from localStorage and updates userClaims redux store
 * @param dispatch redux dispatch function
 */
export function updateClaimsInReduxStore(dispatch: Dispatch, isLogin?: boolean) {
  const newClaims = getUserClaims();

  if (isSome(newClaims)) {
    const agentUserClaims = pipe(
      newClaims.value.userTypes,
      findFirst(eq('$type', UserTypeClaimKey.AgentSite)),
      chain(
        UserTypeClaims.match({
          [UserTypeClaimKey.AgentSite]: agentClaims => {
            const impersonatedAgentClaims = fromNullable(agentClaims.impersonatedAgentClaim);
            return isSome(impersonatedAgentClaims) ? impersonatedAgentClaims : some(agentClaims);
          },
          default: () => none,
        })
      )
    );

    if (isSome(agentUserClaims)) {
      const {agencyAssociations} = agentUserClaims.value;
      let currentSelectedAgency: AgentAgencyClaim | undefined;
      if (agencyAssociations.length > 0) {
        const previousTaxID = agencyAssociations.find(
          agency => agency.maskedIdentifier === sessionStorage.getItem(TokenRefreshSessionKeys.MASKED_IDENTIFIER)
        );
        if (previousTaxID) {
          currentSelectedAgency = previousTaxID;
        } else {
          currentSelectedAgency = agencyAssociations.find(agency => agency.isDefault);
          if (!currentSelectedAgency) {
            currentSelectedAgency = agencyAssociations[0];
          }
        }
        const newYorkStatus = !!(
          localStorage.getItem(TokenRefreshSessionKeys.NEWYORK_STATUS) &&
          localStorage.getItem(TokenRefreshSessionKeys.NEWYORK_STATUS) === 'true'
        );

        const isNyAgency: boolean = currentSelectedAgency.isDefaultShowNewYork
          ? currentSelectedAgency.isDefaultShowNewYork
          : isOnlyNYAgents(currentSelectedAgency);
        let inEmulation = localStorage.getItem(TokenRefreshLocalKey.IN_EMULATION);
        if (isLogin) {
          dispatch(setSelectedAgency(some(currentSelectedAgency), isNyAgency));
        } else {
          let nyStatus: boolean = newYorkStatus
            ? newYorkStatus
            : inEmulation === 'true'
            ? isOnlyNYAgents(currentSelectedAgency)
            : newYorkStatus;
          dispatch(setSelectedAgency(some(currentSelectedAgency), nyStatus));
        }
      }
    }
    dispatch(setUserClaims(fromResult(newClaims.value)));
  }
}

function isOnlyNYAgents(currentSelectedAgency: AgentAgencyClaim) {
  let nyStatus = true;
  if (currentSelectedAgency.agentNumbers.length === 0) {
    return false;
  }

  currentSelectedAgency.agentNumbers.forEach(agent => {
    if (agent.isNewYork === false) {
      nyStatus = false;
    }
  });
  return nyStatus;
}

/**
 * Set the session variable for tracking refreshToken inside iFrame
 * Render refresh spinner
 * Call the refreshToken method (which calls initiateRefreshFlow method from javascript-appfabric)
 */
function* initiateTokenRefreshSaga(action: ReturnType<typeof initiateTokenRefresh>) {
  const {maskedIdentifier} = action.payload;
  setTokenRefreshFlag();
  if (maskedIdentifier) {
    sessionStorage.setItem(TokenRefreshSessionKeys.MASKED_IDENTIFIER, maskedIdentifier);
  }
  yield call(OAuthTokenGeneration.initiateRefreshFlow, getOAuthVariablesForAgentType());
}

function inFrame(providedLogout: string) {
  const isamFrame = document.createElement('iframe');
  isamFrame.setAttribute('src', providedLogout);
  isamFrame.setAttribute('name', 'isam');
  isamFrame.setAttribute('id', 'logout-iframe');
  isamFrame.setAttribute('class', 'logout-iframe');
  document.body.appendChild(isamFrame);
  window.frames['isam'].location = providedLogout;
  isamFrame.onload = () => {
    if (window.frameElement) {
      window.frames['isam'].location = '';
      window.frameElement.remove();
    }
  };
}

function* performLogoutISAM() {
  if (sessionStorage.getItem('is_employee')?.toLocaleLowerCase() === 'false') {
    inFrame(getOAuthProviderVariablesExternal().LOGOUT);
    location.href = getOAuthProviderVariablesExternal().LOGOUT;
  } else {
    inFrame(getOAuthProviderVariablesInternal().LOGOUT);
  }
  localStorage.clear();
  sessionStorage.clear();
  yield put(hideModal());
  yield put(toggleLoginSpinner(false));
  console.log('logged out');
  yield put(push(CoreRouteConstants.LOGIN));
}

/**
 * Fetches new jwt token and updates localStorage
 * @param action the redux action associated with token refresh
 */
function* performTokenRefresh(action: ReturnType<typeof tokenRefresh>) {
  const codeVerifier = sessionStorage.getItem(`login-code-verifier-${action.payload.state}`);
  if (codeVerifier) {
    try {
      const tokenSet: TokenSet = yield call(OAuthTokenGeneration.generateOauthToken, getOAuthVariablesForAgentType());
      if (tokenSet?.access_token) {
        OAuthToken.initialize(tokenSet.access_token);
        const decodedAccessToken = OAuthToken.getDecodedToken();

        let updatedUserClaims: string = decodedAccessToken.Roles;
        if (updatedUserClaims) {
          if (decodedAccessToken.Roles.length === 36) {
            updatedUserClaims = yield call(fetchLargeClaims, tokenSet.access_token);
          }
          updateLocalStorage(tokenSet.access_token, tokenSet.id_token, decodedAccessToken.exp, updatedUserClaims);
          yield put(toggleRefreshSpinner(false, String(), decodedAccessToken.exp * 1000)); // only called to store expiresAt time in global store
          if (shouldRefreshToken() || window.top !== window.self) {
            window.top?.postMessage('refreshToken', window.top.location.origin);
          } else {
            const userClaims = JSON.parse(updatedUserClaims);
            const isEmulating = userClaims.userTypes.some(
              (ut: any) => ut.$type === UserTypeClaimKey.AgentSite && ut.impersonatedAgentClaim
            );
            if (isEmulating && userClaims.exclusivityStatus.name === 'Internal') {
              action.payload.dispatch?.(clearEmulation(userClaims.userName));
            }
            updateClaimsInReduxStore(action.payload.dispatch as Dispatch, true);
            sendDatadogUserInfo();
            yield put(push(CoreRouteConstants.DASHBOARD));
            yield put(toggleLoginSpinner(false));
          }
        } else {
          yield put(toggleLoginSpinner(false));
          yield put(push('/noAccess'));
        }
      }
    } catch (error) {
      yield put(toggleLoginSpinner(false));
      console.log(`${error}`);
    }
  }
}

export function sendDatadogUserInfo() {
  datadogRum.setUser({id: getUserName()['value']});
}

export function* watchInitiateTokenRefresh() {
  yield takeLatest(TokenRefreshActionTypes.INITIATE_TOKEN_REFRESH, initiateTokenRefreshSaga);
}

export function* watchTokenRefresh() {
  yield takeLatest(TokenRefreshActionTypes.REFRESH_TOKEN, performTokenRefresh);
}

export function* watchLogoutToken() {
  yield takeLatest(TokenRefreshActionTypes.LOGOUT, performLogoutISAM);
}

export default function*() {
  yield all([watchInitiateTokenRefresh(), watchTokenRefresh(), watchLogoutToken()]);
}
